import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LayoutList, Briefcase, Settings, Bell, LogOut, ChevronDown, BarChart3, Mail, Megaphone, Inbox as InboxIcon } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { freshnessLabel } from "@/lib/api";

function Logo() {
  return (
    <div className="flex items-center gap-2" data-testid="app-logo">
      <img src="/logo-lovac.png" alt="Lovac" className="h-7 w-auto object-contain" />
      <span className="text-sm font-medium text-faint tracking-tight">Leadgen</span>
    </div>
  );
}

function NavItem({ to, icon: Icon, label, active }) {
  return (
    <Link
      to={to}
      data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
      className={`flex items-center gap-3 px-3 py-2 rounded-[6px] text-sm font-medium transition-colors ${
        active ? "bg-brand-soft text-brand-dark" : "text-subtle hover:bg-background"
      }`}
    >
      <Icon className="w-4 h-4" strokeWidth={1.5} />
      {label}
    </Link>
  );
}

function NotificationBell() {
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const loadCount = () => api.get("/notifications/unread-count").then((r) => setCount(r.data.count)).catch(() => {});

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 20000);
    return () => clearInterval(t);
  }, []);

  const onOpen = (o) => {
    setOpen(o);
    if (o) {
      api.get("/notifications").then((r) => setItems(r.data)).catch(() => {});
      api.post("/notifications/read").then(() => setCount(0)).catch(() => {});
    }
  };

  return (
    <Popover open={open} onOpenChange={onOpen}>
      <PopoverTrigger asChild>
        <button data-testid="notifications-bell" className="relative p-2 rounded-[6px] hover:bg-background transition-colors">
          <Bell className="w-4 h-4 text-subtle" strokeWidth={1.5} />
          {count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-brand text-ink text-[10px] font-bold flex items-center justify-center">
              {count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 bg-white border border-line rounded-[8px]">
        <div className="px-4 py-3 border-b border-line">
          <p className="text-sm font-semibold">Notifiche</p>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 && <p className="px-4 py-6 text-xs text-subtle text-center">Nessuna notifica</p>}
          {items.map((n) => (
            <button
              key={n.id}
              data-testid="notification-item"
              onClick={() => { if (n.opportunity_id) { setOpen(false); navigate(`/opportunita/${n.opportunity_id}`); } }}
              className="w-full text-left px-4 py-3 border-b border-line hover:bg-background transition-colors"
            >
              <p className="text-sm text-ink">{n.text}</p>
              <p className="text-xs text-faint mt-0.5">{freshnessLabel(n.created_at)}</p>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside className="w-56 shrink-0 bg-white border-r border-line flex flex-col fixed h-screen">
        <div className="px-4 h-14 flex items-center border-b border-line">
          <Logo />
        </div>
        <nav className="flex-1 p-3 flex flex-col gap-1">
          <NavItem to="/" icon={LayoutList} label="Opportunità" active={path === "/" || path.startsWith("/opportunita")} />
          <NavItem to="/dashboard" icon={BarChart3} label="Dashboard" active={path.startsWith("/dashboard")} />
          <NavItem to="/posta" icon={InboxIcon} label="Posta" active={path.startsWith("/posta")} />
          {user?.role === "admin" && (
            <>
              <NavItem to="/campagne" icon={Megaphone} label="Campagne" active={path.startsWith("/campagne")} />
              <NavItem to="/posizioni" icon={Briefcase} label="Posizioni" active={path.startsWith("/posizioni")} />
              <NavItem to="/admin" icon={Settings} label="Amministrazione" active={path.startsWith("/admin")} />
            </>
          )}
        </nav>
        <div className="p-3 border-t border-line">
          <div className="flex items-center gap-2 px-1">
            <div className="w-8 h-8 rounded-full bg-brand-soft text-brand-dark flex items-center justify-center text-xs font-semibold">
              {(user?.name || "?").slice(0, 1)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-faint capitalize">{user?.role}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 ml-56 flex flex-col min-h-screen min-w-0">
        <header className="h-14 bg-white border-b border-line flex items-center justify-end gap-2 px-6 sticky top-0 z-10">
          <NotificationBell />
          <Popover>
            <PopoverTrigger asChild>
              <button data-testid="user-menu" className="flex items-center gap-2 px-2 py-1.5 rounded-[6px] hover:bg-background transition-colors">
                <span className="text-sm font-medium">{user?.name}</span>
                <ChevronDown className="w-4 h-4 text-subtle" strokeWidth={1.5} />
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-44 p-1 bg-white border border-line rounded-[8px]">
              <button
                data-testid="profile-link"
                onClick={() => navigate("/profilo")}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-[6px] text-sm text-ink hover:bg-background transition-colors"
              >
                <Mail className="w-4 h-4 text-subtle" strokeWidth={1.5} /> Profilo · Email
              </button>
              <button
                data-testid="logout-button"
                onClick={() => { logout(); navigate("/login"); }}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-[6px] text-sm text-ink hover:bg-background transition-colors"
              >
                <LogOut className="w-4 h-4 text-subtle" strokeWidth={1.5} /> Esci
              </button>
            </PopoverContent>
          </Popover>
        </header>
        <main className="flex-1 p-6 min-w-0">{children}</main>
      </div>
    </div>
  );
}
